import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Send, RotateCcw, Eye, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/proApi";
import { ReminderPanel } from "@/components/ReminderPanel";

const EMAIL_STATUS = {
  pending: "En attente", sending: "Envoi en cours", accepted: "Accepté par le fournisseur",
  failed: "En échec", manual_sent: "Envoyé depuis votre messagerie",
};
const CODE_STATUS = { active: "Actif", suspended: "Suspendu", revoked: "Révoqué" };
const KIND = { welcome: "Bienvenue", campaign: "Campagne clients existants" };
const d = (v) => (v ? new Date(v).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

const Badge = ({ s, map, testid }) => (
  <span data-testid={testid} className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${
    s === "failed" || s === "revoked" ? "bg-red-100 text-red-700" : s === "accepted" || s === "manual_sent" || s === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
    {map[s] || s || "—"}
  </span>
);

const TestPanel = ({ info }) => {
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const send = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await adminApi.post("/admin/pro/personal-codes/test-email", { kind });
      data.status === "accepted" ? toast.success(`Test accepté par le fournisseur, envoyé à ${data.email} (code ${data.code})`) : toast.error(`Échec : ${data.error}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const show = async (kind) => setPreview((await adminApi.get(`/admin/pro/personal-codes/preview?kind=${kind}`)).data);
  return (
    <section data-testid="codes-test-panel" className="rounded-2xl bg-white border border-[var(--oz-border)] p-6">
      <h2 className="font-semibold">1. E-mail de test</h2>
      <p className="mt-1 text-sm text-[var(--oz-muted)]">Envoyé à <b data-testid="codes-admin-email">{info.admin_email || "aucune adresse configurée"}</b>, avec un code de démonstration qui ne donne aucune remise.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {Object.entries(KIND).map(([k, label]) => (
          <React.Fragment key={k}>
            <button onClick={() => send(k)} disabled={!!busy || !info.admin_email} data-testid={`codes-test-send-${k}`}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--oz-dark)] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
              <Send className="w-4 h-4" /> {busy === k ? "Envoi…" : `Tester « ${label} »`}
            </button>
            <button onClick={() => show(k)} data-testid={`codes-preview-${k}`} className="inline-flex items-center gap-2 rounded-full border border-[var(--oz-border)] px-4 py-2 text-sm">
              <Eye className="w-4 h-4" /> Aperçu
            </button>
          </React.Fragment>
        ))}
      </div>
      {preview && <div className="mt-4">
        <p className="text-sm font-semibold" data-testid="codes-preview-subject">Objet : {preview.subject}</p>
        <iframe title="Aperçu" data-testid="codes-preview-frame" srcDoc={preview.html} className="mt-2 w-full h-[520px] rounded-xl border border-[var(--oz-border)]" />
      </div>}
    </section>
  );
};

const Campaign = () => {
  const [c, setC] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => adminApi.get("/admin/pro/campaign").then(({ data }) => setC(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!c) return null;
  const validate = async () => {
    try { setC((await adminApi.post("/admin/pro/campaign/validate", { confirm: true })).data); toast.success("Campagne validée : codes attribués"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sent = async (uid) => {
    try { await adminApi.post(`/admin/pro/campaign/${uid}/sent`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const email = async (uid, to) => {
    setBusy(uid);
    try { await adminApi.post(`/admin/pro/campaign/${uid}/send-email`, to ? { to } : {}); toast.success("E-mail accepté par le fournisseur"); } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); load(); }
  };
  const emailOther = (uid) => { const to = window.prompt("Envoyer le code de ce client à quelle adresse ?"); if (to) email(uid, to.trim()); };
  return (
    <section data-testid="codes-campaign" className="rounded-2xl bg-white border border-[var(--oz-border)] p-6">
      <h2 className="font-semibold">2. Campagne clients pros déjà inscrits</h2>
      <p className="mt-1 text-sm text-[var(--oz-muted)]" data-testid="codes-campaign-counts">
        {c.eligible} compte(s) éligible(s) · {c.codes_existing} code(s) réutilisé(s) · {c.codes_to_create} code(s) à créer.
        Les comptes ayant déjà reçu l'e-mail de bienvenue sont exclus.
      </p>
      {!c.campaign ? (
        <div className="mt-4 rounded-xl bg-[var(--oz-bg-2)] p-4 text-sm">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="codes-campaign-confirm" className="mt-1" />
            J'ai reçu et vérifié l'e-mail de test. Je valide la campagne : un code personnel est attribué à chaque compte éligible.
          </label>
          <button onClick={validate} disabled={!confirm} data-testid="codes-campaign-validate" className="mt-3 rounded-full bg-[var(--oz-dark)] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50">Valider la campagne</button>
        </div>
      ) : <p className="mt-2 text-xs text-emerald-700" data-testid="codes-campaign-validated">Validée le {d(c.campaign.validated_at)}. Cliquez sur « Écrire » : votre messagerie s'ouvre avec le message et le code du client, puis marquez-le comme envoyé.</p>}
      <div className="mt-4 divide-y divide-[var(--oz-border)]">
        {c.rows.map((r) => (
          <div key={r.user_id} data-testid={`campaign-row-${r.user_id}`} className="py-3 flex flex-wrap items-center gap-3 text-sm">
            <div className="flex-1 min-w-[180px]"><b>{r.first_name}</b> — {r.company_name}<div className="text-xs text-[var(--oz-muted)]">{r.email}</div></div>
            <span className="font-mono text-xs">{r.code || "code à créer"}</span>
            <Badge s={r.send_status} map={EMAIL_STATUS} testid={`campaign-status-${r.user_id}`} />
            {!["accepted", "manual_sent", "sending"].includes(r.send_status) && <button onClick={() => email(r.user_id)} disabled={busy === r.user_id} data-testid={`campaign-email-${r.user_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--oz-dark)] text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"><Send className="w-3.5 h-3.5" /> {busy === r.user_id ? "Envoi…" : "Envoyer par e-mail"}</button>}
            {!["accepted", "manual_sent", "sending"].includes(r.send_status) && <button onClick={() => emailOther(r.user_id)} data-testid={`campaign-email-other-${r.user_id}`} className="text-xs underline">Autre adresse</button>}
            {r.mailto && r.send_status === "pending" && <>
              <a href={r.mailto} data-testid={`campaign-write-${r.user_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] px-3 py-1.5 text-xs font-semibold"><Mail className="w-3.5 h-3.5" /> Écrire</a>
              <button onClick={() => sent(r.user_id)} data-testid={`campaign-mark-sent-${r.user_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--oz-border)] px-3 py-1.5 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Marquer envoyé</button>
            </>}
          </div>
        ))}
        {!c.rows.length && <p className="py-3 text-sm text-[var(--oz-muted)]">Aucun compte éligible.</p>}
      </div>
    </section>
  );
};

const CodeRow = ({ r, reload }) => {
  const setStatus = async (status) => { try { await adminApi.post(`/admin/pro/personal-codes/${r.id}/status`, { status }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const retry = async () => { try { await adminApi.post(`/admin/pro/welcome-emails/${r.user_id}/retry`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <tr data-testid={`code-row-${r.code}`} className="align-top border-t border-[var(--oz-border)]">
      <td className="py-3 pr-3"><b>{r.first_name}</b> {r.last_name}<div className="text-xs text-[var(--oz-muted)]">{r.company_name} · {r.email}</div><div className="text-[10px] text-[var(--oz-muted)]">ID {r.user_id}</div></td>
      <td className="py-3 pr-3 font-mono">{r.code}<div className="text-xs text-[var(--oz-muted)] font-sans">créé {d(r.created_at)}</div></td>
      <td className="py-3 pr-3"><Badge s={r.status} map={CODE_STATUS} testid={`code-status-${r.code}`} /></td>
      <td className="py-3 pr-3" data-testid={`code-uses-${r.code}`}>{r.uses}<div className="text-xs text-[var(--oz-muted)]">{r.orders.map((o) => o.order_number).join(", ")}</div><div className="text-xs text-[var(--oz-muted)]">dernier : {d(r.last_used_at)}</div></td>
      <td className="py-3 pr-3 space-y-1">{r.emails.map((e) => (
        <div key={e.key} className="text-xs"><span className="text-[var(--oz-muted)]">{KIND[e.kind] || e.kind}</span> <Badge s={e.status} map={EMAIL_STATUS} /> {d(e.sent_at || e.attempted_at)} · {e.attempts} tentative(s)
          {e.error && <div className="text-red-600">{e.error}</div>}
          {e.kind === "welcome" && e.status === "failed" && <button onClick={retry} data-testid={`code-retry-${r.code}`} className="ml-1 inline-flex items-center gap-1 underline"><RotateCcw className="w-3 h-3" /> Relancer</button>}
        </div>))}{!r.emails.length && <span className="text-xs text-[var(--oz-muted)]">—</span>}</td>
      <td className="py-3">
        {r.status === "active"
          ? <button onClick={() => setStatus("suspended")} data-testid={`code-suspend-${r.code}`} className="text-xs underline">Suspendre</button>
          : r.status === "suspended" && <button onClick={() => setStatus("active")} data-testid={`code-activate-${r.code}`} className="text-xs underline">Réactiver</button>}
      </td>
    </tr>
  );
};

export default function AdminProCodes() {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => adminApi.get("/admin/pro/personal-codes").then(({ data }) => setInfo(data)).catch((e) => setErr(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-pro-codes" className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-6">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)]" data-testid="codes-back"><ArrowLeft className="w-4 h-4" /> Administration</Link>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl">Codes pros personnels -15 %</h1>
        <p className="mt-2 text-sm text-[var(--oz-muted)]">Un code unique par compte pro validé, réservé à son titulaire, sur les produits (hors livraison), sans expiration, non cumulable.</p>
      </div>
      {err && <p data-testid="codes-error" className="text-sm text-red-600">{err} — <Link to="/admin" className="underline">se connecter</Link></p>}
      {info && <>
        {!info.email_configured && <p className="rounded-xl bg-red-50 text-red-700 p-3 text-sm">Service e-mail non configuré : les e-mails de bienvenue ne peuvent pas partir.</p>}
        <ReminderPanel />
        <TestPanel info={info} />
        <Campaign />
        <section className="rounded-2xl bg-white border border-[var(--oz-border)] p-6 overflow-x-auto">
          <h2 className="font-semibold">3. Tous les codes ({info.codes.length})</h2>
          <table className="mt-3 w-full text-sm min-w-[860px]" data-testid="codes-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-[var(--oz-muted)]"><th className="py-2">Client</th><th>Code</th><th>Statut</th><th>Utilisations</th><th>E-mails</th><th></th></tr></thead>
            <tbody>{info.codes.map((r) => <CodeRow key={r.id} r={r} reload={load} />)}</tbody>
          </table>
        </section>
      </>}
    </div>
  );
}
